import type { CheckedState } from '@radix-ui/react-checkbox'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Render } from '@/components/render-if'
import { Checkbox, Label } from '@/components/ui'
import { TextareaField } from '@/components/ui/textarea'
import { TermosDaProposta } from '@/features/proposta/shared/components/termos-da-proposta'
import { CardEtapaContent, CardEtapaItem } from '@/features/proposta/shared/components/ui/card-etapa'
import { showToast } from '@/infra/sonner'
import type { TAssinaturaContratoEnum } from '@/types/proposta'
import { getAssinaturaContratoKeyMap } from '@/utils/key-map'

const TERMOS_PATH = 'detalhesDaProposta.termosDaProposta'
const OBSERVACOES_PATH = 'detalhesDaProposta.observacoes'

type TTermosObservacoesDaPropostaStepBaseProps = {
  assinaturaContrato: TAssinaturaContratoEnum | ''
  termosDaProposta: string
}

export const TermosObservacoesDaPropostaStepBase = ({
  assinaturaContrato,
  termosDaProposta,
}: TTermosObservacoesDaPropostaStepBaseProps) => {
  const { t } = useTranslation('proposta')
  const { control, watch, setValue } = useFormContext()

  const assinaturaContratoKeyMap = getAssinaturaContratoKeyMap()[assinaturaContrato]

  const handleConfirmarTermos = (value: CheckedState) => {
    if (!assinaturaContrato) {
      showToast({
        message: t('detalhes_proposta.termos_observacoes.termos.erro_assinatura'),
        variant: 'error',
        closeButton: true,
      })
      return
    }

    const textoTermos = t(termosDaProposta, {
      defaultValue: termosDaProposta,
      assinaturaContrato: assinaturaContratoKeyMap,
    })

    setValue(TERMOS_PATH, value ? textoTermos : '')
  }

  return (
    <CardEtapaItem
      titulo={t('detalhes_proposta.termos_observacoes.titulo')}
      descricao={t('detalhes_proposta.termos_observacoes.descricao')}
      completed={Boolean(watch(TERMOS_PATH))}
    >
      <CardEtapaContent className="gap-6">
        <span className="form-label">{t('detalhes_proposta.termos_observacoes.termos.titulo')}</span>
        <Render.If condition={termosDaProposta}>
          {(termos) => <TermosDaProposta termos={termos} assinaturaContrato={assinaturaContratoKeyMap || '-'} />}
        </Render.If>
        <div className="mb-2 flex items-center gap-2">
          <Checkbox
            id="termosDaProposta"
            checked={Boolean(watch(TERMOS_PATH))}
            onCheckedChange={handleConfirmarTermos}
          />
          <Label htmlFor="termosDaProposta" className="text-base leading-6 font-normal text-neutral-600 sm:text-sm">
            {t('detalhes_proposta.termos_observacoes.termos.label')}
          </Label>
        </div>
        <TextareaField
          control={control}
          className="w-full max-w-full p-6"
          containerClassName="block w-full min-w-0"
          maxLength={3_000}
          classNameLabel="font-bold"
          name={OBSERVACOES_PATH}
          label={t('detalhes_proposta.termos_observacoes.observacoes.label')}
          placeholder={t('detalhes_proposta.termos_observacoes.observacoes.placeholder')}
        />
      </CardEtapaContent>
    </CardEtapaItem>
  )
}
